import { all, call, delay, put, select, takeEvery, takeLeading } from 'typed-redux-saga'
import { FormNames } from 'Constants/FormNames'
import {
	errorHandler,
	notifyMessageCreator,
	ErrorCode,
	ResponseError,
	showError,
	fileSlice,
	loadersSlice,
	formsSelectors,
} from 'Modules'
import { RequestParams, ResponseCatalog } from 'Models'
import { FieldNames } from 'Constants/FieldNames'
import { Moment } from 'moment'
import { DATE_FORMAT_FOR_BACKEND } from 'Constants/Global'
import { Deserialize } from 'cerialize'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import { authSelector } from 'Pages/auth'
import { Access } from 'Constants/FunctionAliases'
import {
	BankGuarantee,
	BankGuaranteeBuild,
	BankGuaranteeFull,
	BankGuaranteeTypes,
	BgCreateWarning,
	BgDict,
	bgFormNames,
	bgRequestApi,
	BgRequestNs,
	bgRequestSelector,
	bgRequestSlice,
	bgSignatorySlice,
	BgStatus,
	BgWarning,
	DefaultForm,
	DirectorForm,
	DirectorLeadFIO,
	ERROR_CODE,
	fieldsByFormBlock,
	getBgIdFromLocation,
	getIsCommercialBg,
	LeadFIO,
} from 'Pages/ibro'
import { getLocaleTranslator } from 'Src/locale'
import { takeEveryUnique } from 'Helpers'

export function* handleErrorWithForms(error: ResponseError) {
	if (error?.status !== ErrorCode.e422) return yield* call(errorHandler, error)
	const getForm = yield* select(formsSelectors.getGlobalForm)
	const forms = Object.values(bgFormNames)
		.map((formName: string) => {
			const form = getForm(formName)
			if (!form) return undefined
			return (error?.data?.errors ?? []).some((error) =>
				form.getFieldInstance(Object.keys(error)?.[0])
			)
				? formName
				: undefined
		})
		.filter(Boolean)
		.map((formName) => call(errorHandler, error, formName))
	yield* all(forms.length ? forms : [call(errorHandler, error)])
}

function* getItem(action: BgRequestNs.GetItem) {
	const currentBgId = getBgIdFromLocation(FrontUrls.broker.index_neo)
	if (currentBgId === action.payload) {
		const res = yield* call(bgRequestApi.getBg, action.payload)
		const data = Deserialize(res, BankGuaranteeFull)
		yield* put(bgRequestSlice.actions.getHistory(action.payload))
		yield* put(bgRequestSlice.actions.setItem({ data }))
		yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(res, LeadFIO)))
		yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(res, DirectorLeadFIO)))
		yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(res, DefaultForm)))
		yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(res, DirectorForm)))
	}
}

function* getDefaultItem(action: BgRequestNs.GetItem) {
	const res = yield* call(bgRequestApi.getBg, action.payload)
	const data = Deserialize(res, BankGuaranteeFull)
	yield* put(bgRequestSlice.actions.setItem({ data }))
	yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(res, LeadFIO)))
	yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(res, DefaultForm)))
}

function* getDirectorItem(action: BgRequestNs.GetItem) {
	const res = yield* call(bgRequestApi.getBg, action.payload)
	const data = Deserialize(res, BankGuaranteeFull)
	yield* put(bgRequestSlice.actions.setItem({ data }))
	yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(res, DirectorLeadFIO)))
	yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(res, DirectorForm)))
}

function* getList(action: BgRequestNs.GetList) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { params, clear, callback } = action.payload
		let paramsForApi: RequestParams
		if (params) {
			paramsForApi = params
		} else {
			paramsForApi = yield* select(bgRequestSelector.selectParams)
		}

		paramsForApi = {
			...paramsForApi,
			[FieldNames.STARTDATE_FROM]: (
				paramsForApi[FieldNames.STARTDATE_FROM] as Moment | undefined
			)?.format(DATE_FORMAT_FOR_BACKEND),
			[FieldNames.STARTDATE_TO]: (
				paramsForApi[FieldNames.STARTDATE_TO] as Moment | undefined
			)?.format(DATE_FORMAT_FOR_BACKEND),
			[FieldNames.ARCHIVED]: paramsForApi[FieldNames.WITH_ARCHIVE] ? undefined : false,
		} as unknown as RequestParams

		const res = yield* call(bgRequestApi.getItems, paramsForApi)
		const items = new ResponseCatalog<BankGuarantee>(res)
		items.entries = items.entries.map((item) => Deserialize(item, BankGuarantee))
		if (!clear && paramsForApi.page !== 1) {
			const oldItems = yield* select(bgRequestSelector.selectItems)
			items.entries = oldItems.concat(items.entries)
		}
		yield* put(bgRequestSlice.actions.setList(items))
		yield* put(bgRequestSlice.actions.setMeta(res.meta))
		if (params) yield* put(bgRequestSlice.actions.setRequestParams(params))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getDict() {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(bgRequestApi.getDict)
		const dict = Object.assign(new BgDict(), res)
		yield* put(bgRequestSlice.actions.setDict(dict))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getCalculateCommission(action: BgRequestNs.GetCalculateCommission) {
	try {
		yield* put(bgRequestSlice.actions.setAwaitingCalculateCommission(true))
		yield* call(bgRequestApi.getCalculateCommission, action.payload)
	} catch (error) {
		yield* put(bgRequestSlice.actions.setAwaitingCalculateCommission(false))
		yield* call(errorHandler, error)
	}
}

function* saveRequest(action: BgRequestNs.SaveRequest) {
	try {
		const { hideLoader } = action.payload
		if (!hideLoader) yield* put(loadersSlice.actions.showGlobalLoader())
		const bg = yield* call(bgRequestApi.updateBg, action.payload.data)
		const deserializedBg = Deserialize(bg, BankGuaranteeFull)

		if (action.payload.refreshOnlySingle) {
			yield* put(bgRequestSlice.actions.setItem({ data: deserializedBg, resetNeedSave: true }))
		} else {
			yield* put(bgRequestSlice.actions.updateBgItemList(action.payload.data))
		}
		yield* put(bgRequestSlice.actions.getHistory(action.payload.data.id))
		yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(bg, DefaultForm)))
		yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(bg, DirectorForm)))
		yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(bg, LeadFIO)))
		yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(bg, DirectorLeadFIO)))

		if (action.payload.showNotification) yield* call(notifyMessageCreator, 'common.save.success')
	} catch (error) {
		yield* call(handleErrorWithForms, error as ResponseError)
	} finally {
		const { hideLoader } = action.payload
		if (!hideLoader) yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* buildBg(action: BgRequestNs.SendBuildBg) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data: createData } = action.payload

		const res = yield* call(bgRequestApi.build, createData)

		yield* put(bgRequestSlice.actions.setBuildData(Deserialize(res, BankGuaranteeBuild)))
	} catch (error) {
		const responseError = error as ResponseError
		const errors = responseError?.data?.errors || []
		if (errors.find((item) => item?.dadata === ERROR_CODE.MULTIPLE_RESULT)) {
			yield* put(bgRequestSlice.actions.setKppRequired(true))
			return
		}
		const innNotFound = errors.some(
			(item) => item?.organization_attributes === ERROR_CODE.NOT_FOUND
		)
		const purchaseNotFound = errors.some(
			(item) => item?.purchase_attributes === ERROR_CODE.NOT_FOUND
		)
		const isEqualToPartner = errors.some(
			(item) => item?.principalinn === ERROR_CODE.EQUAL_TO_PARTNER_INN
		)
		const isEisUnavailable = errors.some((item) => item?.eis === ERROR_CODE.ERROR)
		if (innNotFound && purchaseNotFound) {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.INN_PURCHASE_NOT_FOUND))
			)
			return
		}
		if (innNotFound) {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.ORGANIZATION_NOT_FOUND))
			)
			return
		}
		if (purchaseNotFound) {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.PURCHASE_NOT_FOUND))
			)
			return
		}
		if (isEqualToPartner) {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.EQUAL_TO_PARTNER_INN))
			)
			return
		}
		if (isEisUnavailable) {
			yield* put(bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.EIS_ERROR)))
			return
		}
		yield* call(errorHandler, error, FormNames.BROKER.CREATE_BG)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* createBg(action: BgRequestNs.SendCreateBg) {
	try {
		yield* put(bgRequestSlice.actions.setAwaitingProcess(true))
		const res = yield* call(bgRequestApi.createBg, action.payload)
		const isArray = Array.isArray(res)
		if (isArray && res.length > 1) {
			yield* put(
				bgRequestSlice.actions.setListOfCreated(
					res.map((item) => Deserialize(item, BankGuaranteeFull))
				)
			)
		} else {
			yield* put(
				routerActions.push(
					FrontUrls.broker.requests_edit.replace(':id', String(isArray ? res[0]?.id : res?.id))
				)
			)
		}
		const requestParams = yield* select(bgRequestSelector.selectParams)
		yield* put(
			bgRequestSlice.actions.setRequestParams(
				Object.assign(new RequestParams(), requestParams, { page: 1 })
			)
		)
		yield* put(bgRequestSlice.actions.setAwaitingProcess(false))
	} catch (error) {
		const responseError = error as ResponseError
		yield* put(bgRequestSlice.actions.setAwaitingProcess(false))
		const errors = responseError?.data?.errors || []
		const isForbidden = errors.some((item) => JSON.stringify(item).includes(ERROR_CODE.FORBIDDEN))
		if (isForbidden) {
			const messageTranslator = getLocaleTranslator('errors')
			yield* call(showError, messageTranslator('partner_forbidden'))
			return
		}
		if (action.payload?.ptypes === BankGuaranteeTypes.COMMERCIAL) {
			return yield* call(errorHandler, error)
		}
		const bgRequestError = errors.filter((item) =>
			JSON.stringify(item).includes(ERROR_CODE.BG_REQUEST_PREFIX)
		)
		if (bgRequestError.length) {
			const { lot_numbers, customer_inns } = action.payload
			if (lot_numbers?.length || customer_inns?.length) {
				yield* put(
					bgRequestSlice.actions.setBgWarning(
						new BgWarning(BgCreateWarning.MULTI_POLICY_ERROR, bgRequestError)
					)
				)
			} else {
				yield* put(
					bgRequestSlice.actions.setBgWarning(
						new BgWarning(BgCreateWarning.POLICY_ERROR, bgRequestError)
					)
				)
			}
			return
		}

		const isIkzInvalid = errors.some(
			(error) => error[FieldNames.PURCHASEIDENTIFICATCODEIKZ] === ERROR_CODE.INVALID
		)
		if (isIkzInvalid) {
			yield* put(bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.IKZ_INVALID)))
			return
		}

		const isIkzEmpty = errors.some(
			(error) => error[FieldNames.PURCHASEIDENTIFICATCODEIKZ] === ERROR_CODE.IS_REQUIRED
		)
		if (isIkzEmpty) {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.IKZ_IS_REQUIRED))
			)
			return
		}

		const customerError = errors.reduce(
			(accum, error) => error[FieldNames.CUSTOMERINN] || accum,
			''
		)
		if (customerError === 'equal_to_principalinn') {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.EQUAL_TO_PRINCIPAL_INN))
			)
			return
		}

		const isPurchnameEmpty = errors.some(
			(error) => error[FieldNames.PURCHNAME] === ERROR_CODE.IS_REQUIRED
		)
		if (isPurchnameEmpty) {
			yield* put(
				bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.PURCHNAME_IS_REQUIRED))
			)
			return
		}

		yield* put(bgRequestSlice.actions.setBgWarning(new BgWarning(BgCreateWarning.ANY)))
	}
}

function* getDependentFields() {
	try {
		let fields = yield* select(bgRequestSelector.selectDependentFields)
		if (!fields) {
			fields = yield* call(bgRequestApi.getDependentFields)
			yield* put(bgRequestSlice.actions.setDependentFields(fields))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}
// TODO удалить
function* updateCurrentBgState(res: BankGuaranteeFull) {
	const currentBg = yield* select(bgRequestSelector.selectCurrentBg)
	if (!currentBg)
		yield* put(bgRequestSlice.actions.setItem({ data: Deserialize(res, BankGuaranteeFull) }))
}

function* getItemWithCheckStatus(action: BgRequestNs.GetItemWithCheckStatus) {
	try {
		const currentBgId = getBgIdFromLocation(FrontUrls.broker.index)
		if (currentBgId !== action.payload) return
		yield* put(loadersSlice.actions.showGlobalLoader())
		const id = action.payload
		const items = yield* select(bgRequestSelector.selectItems)
		const userAliases = yield* select(authSelector.getUserAliases)
		const canUpdate = userAliases?.includes(Access.broker.update)
		const currentItemFromItems = items?.find((item) => item.id === id)
		const locked_fields = Object.keys(fieldsByFormBlock).flatMap((key) => fieldsByFormBlock[key])
		const full = yield* select(bgRequestSelector.selectCurrentBg)
		let res

		if (currentItemFromItems && full) {
			const needCheckStatus =
				currentItemFromItems.external_id && canUpdate && !full.locked_by_service_request
			if (needCheckStatus) yield* put(loadersSlice.actions.hideGlobalLoader())
			res = needCheckStatus
				? yield* call(bgRequestApi.checkStatus, id)
				: yield* call(bgRequestApi.getBg, id)
		} else {
			res = yield* call(bgRequestApi.getBg, id)
			if (res.external_id && canUpdate && !res.locked_by_service_request) {
				yield* updateCurrentBgState({ ...res, locked_fields })
				yield* put(loadersSlice.actions.hideGlobalLoader())
				yield* delay(2000) // необходима для получения результата запроса статуса
				res = yield* call(bgRequestApi.checkStatus, id)
			}
		}
		yield* put(bgRequestSlice.actions.setItem({ data: Deserialize(res, BankGuaranteeFull) }))
		yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(res, DefaultForm)))
		yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(res, DirectorForm)))
		yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(res, LeadFIO)))
		yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(res, DirectorLeadFIO)))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendRequest(action: BgRequestNs.SendRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data, params } = action.payload
		yield* call(bgRequestApi.updateBg, data)
		let bg
		if (params?.external_id) {
			bg = yield* call(bgRequestApi.updateRequest, data.id, { send_post_documents: true })
			if (params?.guaranteetemplate?.file)
				yield* put(fileSlice.actions.removeRequestedFile(params.guaranteetemplate.file))
		} else if (params?.status?.status_slug === BgStatus.NOVA) {
			bg = yield* call(bgRequestApi.checkLimit, data)
		} else if (getIsCommercialBg(data.ptype)) {
			bg = yield* call(bgRequestApi.createCommercialOrder, data.id)
		} else {
			bg = yield* call(bgRequestApi.postRequest, data.id)
		}
		yield* put(
			bgRequestSlice.actions.setItem({
				data: Deserialize(bg, BankGuaranteeFull),
				resetNeedSave: true,
			})
		)
		yield* put(bgRequestSlice.actions.getHistory(data.id))
		yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(bg, DefaultForm)))
		yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(bg, DirectorForm)))
		yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(bg, LeadFIO)))
		yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(bg, DirectorLeadFIO)))
	} catch (error) {
		yield* call(handleErrorWithForms, error as ResponseError)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getBankData(action: BgRequestNs.GetBankData) {
	try {
		const { id, data } = action.payload
		yield* call(bgRequestApi.getBankData, id, data)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* changeSendAddress(action: BgRequestNs.ChangeSendAddress) {
	try {
		const { id, ...data } = action.payload
		const bg = yield* call(bgRequestApi.changeSendAddress, id, data)
		yield* put(bgRequestSlice.actions.setItem({ data: Deserialize(bg, BankGuaranteeFull) }))
	} catch (error) {
		yield* call(handleErrorWithForms, error as ResponseError)
	}
}

function* updateCurrentElements(action: BgRequestNs.UpdateCurrentElements) {
	try {
		const { id } = action.payload
		const res = yield* call(bgRequestApi.updateCurrentElements, id)
		yield* put(bgRequestSlice.actions.setItem({ data: Deserialize(res, BankGuaranteeFull) }))
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* updateItem(action: BgRequestNs.UpdateCurrentElements) {
	try {
		const currentBgId = getBgIdFromLocation(FrontUrls.broker.index)
		if (currentBgId !== action.payload.id) return
		const res = yield* call(bgRequestApi.getBg, action.payload.id)
		const data = Deserialize(res, BankGuaranteeFull)
		yield* put(bgRequestSlice.actions.setItem({ data }))
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* sendToRework(action: BgRequestNs.SendToRework) {
	try {
		const res = yield* call(bgRequestApi.rework, action.payload.id)
		const data = Deserialize(res, BankGuaranteeFull)
		yield* put(bgRequestSlice.actions.setItem({ data }))
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* addFavorite(action: BgRequestNs.AddFavorite) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id } = action.payload
		yield* call(bgRequestApi.addFavorite, id)
		yield* put(bgRequestSlice.actions.getList({}))
	} catch (e) {
		yield* call(errorHandler, e)
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* deleteFavorite(action: BgRequestNs.DeleteFavorite) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id } = action.payload
		yield* call(bgRequestApi.deleteFavorite, id)
		yield* put(bgRequestSlice.actions.getList({}))
	} catch (e) {
		yield* call(errorHandler, e)
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* assignRequest(action: BgRequestNs.AssignRequest) {
	try {
		const { id, partner_id, created_by_user_id, clear_data } = action.payload
		const res = yield* call(
			bgRequestApi.assignRequest,
			id,
			partner_id,
			created_by_user_id,
			clear_data
		)
		const deserializedOrder = Deserialize(res, BankGuarantee)
		yield* put(bgRequestSlice.actions.updateBgItemList(deserializedOrder))
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* checkLimit(action: BgRequestNs.CheckLimit) {
	try {
		const res = yield* call(bgRequestApi.checkLimit, action.payload.data)

		yield* put(
			bgRequestSlice.actions.setItem({
				data: Deserialize(res, BankGuaranteeFull),
				resetNeedSave: true,
			})
		)
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* updateFlag(action: BgRequestNs.UpdateFlag) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(bgRequestApi.updateBg, action.payload)
		yield* put(bgRequestSlice.actions.setItem({ data: Deserialize(res, BankGuaranteeFull) }))
		yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(res, DefaultForm)))
		yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(res, DirectorForm)))
		yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(res, LeadFIO)))
		yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(res, DirectorLeadFIO)))
	} catch (e) {
		yield* call(errorHandler, e)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getSpk(action: BgRequestNs.GetSpk) {
	try {
		yield* put(bgRequestSlice.actions.setSpkLoading(true))

		const res = yield* call(bgRequestApi.svetoforCheck, action.payload)

		yield* put(bgRequestSlice.actions.setSpkResponse(res))
	} catch (error) {
		yield* put(bgRequestSlice.actions.setSpkResponse([]))
	} finally {
		yield* put(bgRequestSlice.actions.setSpkLoading(false))
	}
}

function* sendReleasedDocs(action: BgRequestNs.SendReleasedDocs) {
	try {
		const { id, params } = action.payload
		yield* call(bgRequestApi.sendReleaseDocs, id, params)
		yield* call(notifyMessageCreator, 'ibro.send_released_docs.success')
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* createPublication(action: BgRequestNs.CreatePublication) {
	try {
		const { data, callback } = action.payload
		const res = yield* call(bgRequestApi.createPublication, data)
		yield* put(bgRequestSlice.actions.setPublication(res))
		callback()
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* updatePublication(action: BgRequestNs.UpdatePublication) {
	try {
		const { id, data, callback } = action.payload
		const res = yield* call(bgRequestApi.updatePublication, id, data)
		yield* put(bgRequestSlice.actions.setPublication(res))
		callback()
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* deletePublication(action: BgRequestNs.DeletePublication) {
	try {
		const { id, callback } = action.payload
		yield* call(bgRequestApi.deletePublication, id)
		yield* put(bgRequestSlice.actions.clearPublication())
		callback()
	} catch (e) {
		yield* call(errorHandler, e)
	}
}

function* getHistory(action: BgRequestNs.GetHistory) {
	try {
		const request_id = action.payload
		const res = yield* call(bgRequestApi.getHistory, request_id)
		yield* put(bgRequestSlice.actions.setHistory({ currentId: request_id, entries: res.entries }))
	} catch (e) {
		yield* put(bgRequestSlice.actions.setHistory(null))
	}
}

export function* bgRequestSaga() {
	yield* takeLeading(bgRequestSlice.actions.getList.type, getList)
	yield* takeLeading(bgRequestSlice.actions.getDict.type, getDict)

	yield* takeLeading(bgRequestSlice.actions.buildBg.type, buildBg)
	yield* takeLeading(bgRequestSlice.actions.createBg.type, createBg)

	yield* takeLeading(bgRequestSlice.actions.getItem.type, getItem)
	yield* takeLeading(bgRequestSlice.actions.getItemWithCheckStatus.type, getItemWithCheckStatus)
	yield* takeEvery(bgRequestSlice.actions.getDependentFields.type, getDependentFields)

	yield* takeLeading(bgRequestSlice.actions.saveRequest.type, saveRequest)
	yield* takeEvery(bgRequestSlice.actions.sendRequest.type, sendRequest)

	yield* takeLeading(bgRequestSlice.actions.getBankData.type, getBankData)
	yield* takeEvery(bgRequestSlice.actions.getCalculateCommission.type, getCalculateCommission)

	yield* takeEvery(bgRequestSlice.actions.changeSendAddress.type, changeSendAddress)

	yield* takeEvery(bgRequestSlice.actions.updateCurrentElements.type, updateCurrentElements)
	yield* takeLeading(bgRequestSlice.actions.sendToRework.type, sendToRework)
	yield* takeLeading(bgRequestSlice.actions.assignRequest.type, assignRequest)

	yield* takeLeading(bgRequestSlice.actions.updateItem.type, updateItem)

	yield* takeLeading(bgRequestSlice.actions.addFavorite.type, addFavorite)
	yield* takeLeading(bgRequestSlice.actions.deleteFavorite.type, deleteFavorite)
	yield* takeLeading(bgRequestSlice.actions.checkLimit.type, checkLimit)

	yield* takeLeading(bgRequestSlice.actions.updateFlag.type, updateFlag)

	yield* takeLeading(bgRequestSlice.actions.getDefaultItem.type, getDefaultItem)
	yield* takeLeading(bgRequestSlice.actions.getDirectorItem.type, getDirectorItem)

	yield* takeLeading(bgRequestSlice.actions.getSpk.type, getSpk)
	yield* takeEvery(bgRequestSlice.actions.sendReleasedDocs.type, sendReleasedDocs)

	yield* takeEveryUnique(bgRequestSlice.actions.createPublication.type, createPublication)
	yield* takeEveryUnique(bgRequestSlice.actions.updatePublication.type, updatePublication)
	yield* takeEveryUnique(bgRequestSlice.actions.deletePublication.type, deletePublication)
	yield* takeEvery(bgRequestSlice.actions.getHistory.type, getHistory)
}
